import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNotice } from '../../components/notice.js';
import { Button, Modal, Mono } from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { ManagerApproval, approvalRefusal, isOverridable } from '../auth/manager-approval.js';
import type { ManagerOverride } from '../auth/manager-approval.js';
import { sizeGrids } from '../purchasing/size-grid.js';
import { COUNT_REASONS, countChanges, whyNot } from './adjust-counts.js';
import type { CountReason } from './adjust-counts.js';

/**
 * Adjusting a style's stock, as a grid (SCOPE §6.4, decision 102; FB-1116).
 *
 * The client: "the adjust stock button requires each individual variant to be
 * selected, ideally a grid so we can adjust stock on a bunch of variants at
 * once." So the style's sizes are laid out the way every other grid lays them
 * — colors down, sizes across, one grid per length — and each box holds what is
 * on the shelf, starting at what the books say. Type the count that is there;
 * the change is the difference. One cause covers the lot (adjust-counts.ts).
 *
 * A box selects its number when pressed, so typing 2 makes it 2 — not 12
 * (the app-wide rule, select-on-focus.ts).
 */

export interface AdjustableVariant {
  readonly id: string;
  readonly sku: string;
  readonly colorName: string | null;
  readonly sizeLabel: string | null;
  readonly altSize: string | null;
  readonly sizeSort?: number;
  readonly onHand: number;
}

export function AdjustStockDialog({
  productId,
  styleName,
  variants,
  onClose,
  onAdded,
}: {
  productId: string;
  /** For the notice that says what was adjusted. */
  styleName: string;
  variants: readonly AdjustableVariant[];
  onClose: () => void;
  /**
   * Units that went up, by size — offered as labels to print (FB-1116). That
   * offer says what was added, so the notice is left to an adjustment without one.
   */
  onAdded?: ((added: Record<string, number>) => void) | undefined;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [reason, setReason] = useState<CountReason | ''>('');
  const [note, setNote] = useState('');
  const [needsApproval, setNeedsApproval] = useState(false);

  const changes = countChanges(variants, typed);
  const blocked = whyNot({ variants, typed, reason, note });
  const grids = sizeGrids(variants);

  const save = useMutation({
    mutationFn: (override?: ManagerOverride) =>
      api.products.adjustStockCounts.mutate({
        reason: reason as CountReason,
        note: note.trim(),
        lines: changes.map((change) => ({ variantId: change.variantId, counted: change.to })),
        ...(override === undefined ? {} : { override }),
      }),
    onSuccess: () => {
      recordBreadcrumb('stock', `adjusted stock: ${reason} on ${changes.length} sizes`);
      void queryClient.invalidateQueries({ queryKey: ['products', 'get', productId] });
      void queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      const added = Object.fromEntries(
        changes
          .filter((change) => change.to > change.from)
          .map((change) => [change.variantId, change.to - change.from]),
      );
      onClose();
      if (Object.keys(added).length > 0 && onAdded !== undefined) {
        onAdded(added);
        return;
      }
      const up = changes.reduce((sum, change) => sum + Math.max(0, change.to - change.from), 0);
      const down = changes.reduce((sum, change) => sum + Math.max(0, change.from - change.to), 0);
      const moved = [up > 0 ? `+${up}` : null, down > 0 ? `−${down}` : null].filter(
        (part): part is string => part !== null,
      );
      notify({
        title: `Stock adjusted — ${styleName}, ${moved.join(' / ')} ${
          up + down === 1 ? 'unit' : 'units'
        }`,
      });
    },
    onError: (error) => {
      if (isOverridable(error)) setNeedsApproval(true);
    },
  });

  if (needsApproval) {
    return (
      <ManagerApproval
        title="Manager approval needed"
        because="Changing stock by hand changes what the books say the shop owns."
        busy={save.isPending}
        error={save.isError ? approvalRefusal(save.error) : null}
        onCancel={onClose}
        onApprove={(override) => save.mutate(override)}
      />
    );
  }

  return (
    <Modal
      open
      wide
      title="Adjust stock"
      description="Type what is on the shelf for each size that is wrong. Receiving and sales keep their own records; this is for everything else."
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={save.isPending}
          disabledReason={blocked}
          onClick={() => save.mutate(undefined)}
        >
          {changes.length === 0
            ? 'Save'
            : `Save · ${changes.length} size${changes.length === 1 ? '' : 's'}`}
        </Button>
      }
    >
      {grids.map((grid) => (
        <div className="table-scroll style-labels__table" key={grid.altSize ?? 'one'}>
          <table className="table matrix">
            {grid.label !== null ? (
              <caption className="matrix__caption">{grid.label}</caption>
            ) : null}
            <thead>
              <tr>
                <th />
                {grid.sizes.map((label, index) => (
                  <th key={label ?? `size-${index}`} className="num">
                    {label ?? 'One size'}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.rows.map((row) => (
                <tr key={row.key}>
                  <th scope="row" className="matrix__row-head">
                    {row.label}
                  </th>
                  {row.cells.map((variant, index) => {
                    if (variant === undefined) return <td key={index} />;
                    const change = changes.find((entry) => entry.variantId === variant.id);
                    return (
                      <td key={variant.id} className="matrix__cell">
                        <input
                          className="price-input num"
                          inputMode="numeric"
                          aria-label={`On the shelf, ${variant.sku}`}
                          value={typed[variant.id] ?? String(variant.onHand)}
                          onChange={(event) =>
                            setTyped((current) => ({
                              ...current,
                              [variant.id]: event.target.value,
                            }))
                          }
                        />
                        <span className="style-labels__onhand">
                          <Mono>{variant.sku}</Mono> · was {variant.onHand}
                          {change === undefined ? null : (
                            <strong className="adjust__change">
                              {' '}
                              {change.to > change.from ? '+' : '−'}
                              {Math.abs(change.to - change.from)}
                            </strong>
                          )}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <fieldset className="feedback-severity">
        <legend className="field__label">What happened</legend>
        {COUNT_REASONS.map((entry) => (
          <label key={entry.value} className="feedback-severity__option">
            <input
              type="radio"
              name="adjust-reason"
              checked={reason === entry.value}
              onChange={() => setReason(entry.value)}
            />
            <span>
              {entry.label}
              <span className="text-secondary"> — {entry.hint}</span>
            </span>
          </label>
        ))}
      </fieldset>

      <label className="field">
        <span className="field__label">What happened, in a sentence</span>
        <input
          className="input"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="e.g. shelf counted after the weekend — two larges missing"
          maxLength={200}
        />
      </label>
    </Modal>
  );
}
